import { create } from 'zustand'

interface UIState {
  // Sidebar
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void

  // Modals
  modals: Record<string, boolean>
  openModal: (id: string) => void
  closeModal: (id: string) => void
  closeAllModals: () => void

  // Drawers
  drawers: Record<string, boolean>
  openDrawer: (id: string) => void
  closeDrawer: (id: string) => void

  // Toasts (handled by react-hot-toast, but we can track)
  toasts: Array<{ id: string; message: string; type: 'success' | 'error' | 'warning' | 'info' }>
  addToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  removeToast: (id: string) => void

  // Loading states
  globalLoading: boolean
  setGlobalLoading: (loading: boolean) => void

  // Page transitions
  pageTransition: boolean
  setPageTransition: (transition: boolean) => void

  // Search/Command palette
  commandPaletteOpen: boolean
  toggleCommandPalette: () => void
}

export const useUIStore = create<UIState>((set) => ({
  // Sidebar
  sidebarOpen: true,
  sidebarCollapsed: false,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
  setSidebarCollapsed: (collapsed: boolean) => set({ sidebarCollapsed: collapsed }),

  // Modals
  modals: {},
  openModal: (id: string) => set((state) => ({ modals: { ...state.modals, [id]: true } })),
  closeModal: (id: string) => set((state) => {
    const newModals = { ...state.modals }
    delete newModals[id]
    return { modals: newModals }
  }),
  closeAllModals: () => set({ modals: {} }),

  // Drawers
  drawers: {},
  openDrawer: (id: string) => set((state) => ({ drawers: { ...state.drawers, [id]: true } })),
  closeDrawer: (id: string) => set((state) => {
    const newDrawers = { ...state.drawers }
    delete newDrawers[id]
    return { drawers: newDrawers }
  }),

  // Toasts
  toasts: [],
  addToast: (message: string, type = 'info') => set((state) => ({
    toasts: [...state.toasts, { id: Date.now().toString(), message, type }]
  })),
  removeToast: (id: string) => set((state) => ({
    toasts: state.toasts.filter(t => t.id !== id)
  })),

  // Global loading
  globalLoading: false,
  setGlobalLoading: (loading: boolean) => set({ globalLoading: loading }),

  // Page transitions
  pageTransition: false,
  setPageTransition: (transition: boolean) => set({ pageTransition: transition }),

  // Command palette
  commandPaletteOpen: false,
  toggleCommandPalette: () => set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),
}))

// Selectors
export const useSidebar = () => useUIStore(state => ({
  open: state.sidebarOpen,
  collapsed: state.sidebarCollapsed,
  toggle: state.toggleSidebar,
  setOpen: state.setSidebarOpen,
  setCollapsed: state.setSidebarCollapsed,
}))

export const useModals = () => useUIStore(state => ({
  modals: state.modals,
  open: state.openModal,
  close: state.closeModal,
  closeAll: state.closeAllModals,
}))

export const useDrawers = () => useUIStore(state => ({
  drawers: state.drawers,
  open: state.openDrawer,
  close: state.closeDrawer,
}))

export const useGlobalLoading = () => useUIStore(state => ({
  loading: state.globalLoading,
  setLoading: state.setGlobalLoading,
}))

export const useCommandPalette = () => useUIStore(state => ({
  open: state.commandPaletteOpen,
  toggle: state.toggleCommandPalette,
}))